import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';
import type { AuthProvider, SignInResult, SignUpInput } from './types';

/** Autenticazione con Amazon Cognito (SRP), MFA TOTP obbligatoria. */
export class CognitoAuth implements AuthProvider {
  private pool: CognitoUserPool;
  private pending: CognitoUser | null = null;

  constructor(userPoolId: string, clientId: string) {
    this.pool = new CognitoUserPool({ UserPoolId: userPoolId, ClientId: clientId });
  }

  private user(email: string): CognitoUser {
    return new CognitoUser({ Username: email.trim().toLowerCase(), Pool: this.pool });
  }

  signUp(i: SignUpInput): Promise<void> {
    const attrs = [
      new CognitoUserAttribute({ Name: 'email', Value: i.email.trim().toLowerCase() }),
      new CognitoUserAttribute({ Name: 'given_name', Value: i.firstName.trim() }),
      new CognitoUserAttribute({ Name: 'family_name', Value: i.lastName.trim() }),
    ];
    return new Promise((resolve, reject) =>
      this.pool.signUp(i.email.trim().toLowerCase(), i.password, attrs, [], (err) => (err ? reject(err) : resolve())),
    );
  }

  confirmSignUp(email: string, code: string): Promise<void> {
    return new Promise((resolve, reject) => this.user(email).confirmRegistration(code.trim(), true, (err) => (err ? reject(err) : resolve())));
  }

  resendSignUpCode(email: string): Promise<void> {
    return new Promise((resolve, reject) => this.user(email).resendConfirmationCode((err) => (err ? reject(err) : resolve())));
  }

  private callbacks(resolve: (r: SignInResult) => void, reject: (e: unknown) => void) {
    return {
      onSuccess: () => resolve({ status: 'signed_in' as const }),
      onFailure: (err: { code?: string }) =>
        err?.code === 'UserNotConfirmedException' ? resolve({ status: 'email_not_verified' }) : reject(err),
      totpRequired: () => resolve({ status: 'mfa_required' }),
      mfaRequired: () => resolve({ status: 'mfa_required' }),
      mfaSetup: () => resolve({ status: 'mfa_setup_required' }),
    };
  }

  signIn(email: string, password: string): Promise<SignInResult> {
    const u = this.user(email);
    this.pending = u;
    return new Promise((resolve, reject) =>
      u.authenticateUser(new AuthenticationDetails({ Username: email.trim().toLowerCase(), Password: password }), this.callbacks(resolve, reject)),
    );
  }

  submitMfaCode(code: string): Promise<SignInResult> {
    const u = this.pending;
    if (!u) return Promise.reject(new Error('Sessione di accesso scaduta, ripeti l’accesso'));
    return new Promise((resolve, reject) => u.sendMFACode(code.trim(), this.callbacks(resolve, reject), 'SOFTWARE_TOKEN_MFA'));
  }

  beginTotpSetup(): Promise<{ secret: string; uri: string }> {
    const u = this.pending ?? this.pool.getCurrentUser();
    if (!u) return Promise.reject(new Error('Sessione scaduta, ripeti l’accesso'));
    this.pending = u;
    return new Promise((resolve, reject) =>
      u.associateSoftwareToken({
        associateSecretCode: (secret: string) =>
          resolve({ secret, uri: `otpauth://totp/Yardpay:${encodeURIComponent(u.getUsername())}?secret=${secret}&issuer=Yardpay` }),
        onFailure: reject,
      }),
    );
  }

  verifyTotpSetup(code: string): Promise<SignInResult> {
    const u = this.pending;
    if (!u) return Promise.reject(new Error('Sessione scaduta, ripeti l’accesso'));
    return new Promise((resolve, reject) =>
      u.verifySoftwareToken(code.trim(), 'Yardpay', {
        onSuccess: () => {
          u.setUserMfaPreference(null, { PreferredMfa: true, Enabled: true }, () => resolve({ status: 'signed_in' }));
        },
        onFailure: reject,
      }),
    );
  }

  forgotPassword(email: string): Promise<void> {
    return new Promise((resolve, reject) =>
      this.user(email).forgotPassword({ onSuccess: () => resolve(), onFailure: reject, inputVerificationCode: () => resolve() }),
    );
  }

  confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
    return new Promise((resolve, reject) =>
      this.user(email).confirmPassword(code.trim(), newPassword, { onSuccess: () => resolve(), onFailure: reject }),
    );
  }

  private session(): Promise<{ user: CognitoUser; session: CognitoUserSession } | null> {
    const u = this.pool.getCurrentUser();
    if (!u) return Promise.resolve(null);
    return new Promise((resolve) =>
      u.getSession((err: Error | null, s: CognitoUserSession | null) => resolve(err || !s?.isValid() ? null : { user: u, session: s })),
    );
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    const s = await this.session();
    if (!s) throw new Error('Sessione scaduta');
    await new Promise<void>((resolve, reject) => s.user.changePassword(oldPassword, newPassword, (err) => (err ? reject(err) : resolve())));
  }

  async getIdToken(): Promise<string | null> {
    const s = await this.session();
    return s ? s.session.getIdToken().getJwtToken() : null;
  }

  currentEmail(): string | null {
    return this.pool.getCurrentUser()?.getUsername() ?? null;
  }

  async signOut(everywhere = false): Promise<void> {
    const s = await this.session();
    if (s && everywhere) {
      await new Promise<void>((resolve) => s.user.globalSignOut({ onSuccess: () => resolve(), onFailure: () => resolve() }));
    }
    this.pool.getCurrentUser()?.signOut();
    this.pending = null;
  }
}
